import { Fragment, Node as ProseNode, Slice } from "prosemirror-model";
import { EditorState, Plugin, PluginKey } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";
import invariant from "tiny-invariant";
import { v4 } from "uuid";

import * as sets from "~/lib/sets";

import { Extension } from "../extension";
import { HEADING_MAX_LEVEL } from "../nodes/header";
import { blockDescendants } from "../utils";
import { ASSIGNABLE_TYPE_NAMES } from "./assignable";

/*
ProseMirror uses positions into a document to determine where to conduct operations.
But a lot of our operations are conducted on "block"s, identified by their `keys`s.
so we need a way to convert a block's key into a position, which requires a traversal of the whole document.

This plugin does that traversal when the document changes
and caches the information to be used by other plugins,
so they don't need to traverse the document themselves.

This plugin also builds a tree-like representation of the document derived from the blocks' indentation levels,
which allows users to access some more meta information for the blocks, such as their children and parent.
**/

export type Boundary = [start: number, end: number, node: ProseNode];
export type Boundaries = { [blockId: string]: Boundary };

function getEffectiveIndent(block: ProseNode) {
	let indent = block.attrs.indent ?? 0;
	if (indent < 0) {
		// Sometimes when pasting content from external sources (eg. Slack), the parsed indent will be negative.
		// Setting them to 0 because they are displayed the same as having an indent of 0
		indent = 0;
	}
	if (block.type.name === "header-one") {
		// H2 blocks are children of H1, and other blocks are children of H2
		const level = Math.max(Math.min(Number(block.attrs.level), 2), 1);
		return indent - (HEADING_MAX_LEVEL - level + 1);
	}
	if (ASSIGNABLE_TYPE_NAMES.includes(block.type.name)) return indent + 1;
	return indent;
}

export class BlockTreeNode {
	children: BlockTreeNode[] = [];
	start = 0;
	indent = 0;
	block: ProseNode | null = null;
	indexFromParent = -1;
	depth: number;
	/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
	meta: { [key: string]: any } = {};
	private lastChildByType: { [key: string]: BlockTreeNode } = {};
	private lastChildByTypeAndIndent: { [key: string]: BlockTreeNode } = {};
	prevNodeOfSameType: BlockTreeNode | null = null;
	prevNodeOfSameTypeAndIndent: BlockTreeNode | null = null;
	prevSibling: BlockTreeNode | null = null;
	nextSibling: BlockTreeNode | null = null;
	constructor(public parent?: BlockTreeNode) {
		this.depth = parent ? parent.depth + 1 : 0;
	}
	addChildBlock(block: ProseNode, start: number): BlockTreeNode {
		const typeName = block.type.name;
		const childNode = new BlockTreeNode();
		const childIndent = getEffectiveIndent(block);
		childNode.block = block;
		childNode.indent = childIndent;
		childNode.start = start;
		childNode.parent = this;
		childNode.indexFromParent = this.children.push(childNode) - 1;
		if (childNode.indexFromParent !== 0) {
			childNode.prevSibling = this.children[childNode.indexFromParent - 1];
			childNode.prevSibling.nextSibling = childNode;
		}
		const prevSameType = this.lastChildByType[typeName];
		const prevSameTypeAndIndent = this.lastChildByTypeAndIndent[`${typeName}|${childIndent}`];
		if (prevSameType) {
			childNode.prevNodeOfSameType = prevSameType;
		}
		if (prevSameTypeAndIndent) {
			childNode.prevNodeOfSameTypeAndIndent = prevSameTypeAndIndent;
		}
		this.lastChildByType[typeName] = childNode;
		this.lastChildByTypeAndIndent[`${typeName}|${childIndent}`] = childNode;
		return childNode;
	}
	get prevNode(): BlockTreeNode | null {
		if (this.parent && this.indexFromParent > 0) return this.parent.children[this.indexFromParent - 1];
		return null;
	}

	/** Iterate over all descendants of this node */
	*descendants(): Generator<BlockTreeNode, void, undefined> {
		for (const child of this.children) {
			yield child;
			yield* child.descendants();
		}
	}

	*containedBlocks() {
		for (const c of this.descendants()) {
			if (c.block) {
				yield c.block;
			}
		}
	}

	get lastChild(): BlockTreeNode | null {
		const index = this.children.length - 1;
		if (index >= 0) return this.children[index];
		return null;
	}

	get textContent(): string {
		const frag = Fragment.fromArray([...this.containedBlocks()]);
		return frag.textBetween(0, frag.size, "\n", "\n");
	}

	get canHaveChildren(): boolean {
		const leafBlockTypes = ["image_strip", "embed", "key_result", "horizontal_rule"];
		return this.block !== null && !leafBlockTypes.includes(this.block.type.name);
	}

	get after(): number {
		let current;
		for (current = this; current.children.length; current = current.children[current.children.length - 1]);
		return current.start + (current.block ? current.block.nodeSize : 0);
	}
	get size(): number {
		return this.after - this.start;
	}
}

export class BlockTree {
	constructor(
		public root: BlockTreeNode,
		private blockKeyToNodeMap: { [key: string]: BlockTreeNode },
	) {}
	getNodeByBlockKey = (blockKey: string): BlockTreeNode | undefined => {
		return this.blockKeyToNodeMap[blockKey];
	};
	get length() {
		return Object.keys(this.blockKeyToNodeMap).length;
	}
}

export function buildTree(content: ProseNode | Slice | Fragment) {
	const blockKeyToNodeMap: { [key: string]: BlockTreeNode } = {};
	const root = new BlockTreeNode();
	// The root's "indent" is 1 less than the largest heading, so that all H1s
	// are children of it.
	root.indent = -HEADING_MAX_LEVEL - 1;
	const parents: BlockTreeNode[] = [root];
	function getLastParent() {
		return parents[parents.length - 1];
	}
	blockDescendants(content, (block, start) => {
		const lastParent = getLastParent();
		const previousBlockTreeNode = lastParent.lastChild;
		const blockEffectiveIndent = getEffectiveIndent(block);
		let childNode: BlockTreeNode;

		// If the block is indented more than the previous block, it's a child
		if (
			previousBlockTreeNode &&
			blockEffectiveIndent > previousBlockTreeNode.indent &&
			previousBlockTreeNode.canHaveChildren
		) {
			parents.push(previousBlockTreeNode);
			childNode = previousBlockTreeNode.addChildBlock(block, start);
		}
		// If the block is indented less than the previous block,
		// pop the previous parents until one can be a parent of the block
		else if (previousBlockTreeNode && blockEffectiveIndent < previousBlockTreeNode.indent) {
			let parent = getLastParent();
			while (parent.indent >= blockEffectiveIndent && parents.length > 0) {
				parents.pop();
				parent = getLastParent();
			}
			childNode = parent.addChildBlock(block, start);
		}
		// Otherwise, it's a sibling of the previous block
		else {
			childNode = lastParent.addChildBlock(block, start);
		}

		if (block.attrs.blockId) {
			blockKeyToNodeMap[block.attrs.blockId] = childNode;
		}
		return;
	});
	return new BlockTree(root, blockKeyToNodeMap);
}

function nodeChildrenBlockIds(node: BlockTreeNode) {
	return new Set<string>(node.children.filter(c => c.block).map(c => (invariant(c.block), c.block.attrs.blockId)));
}

/**
 * Compute the non-leaf nodes that have differing children between oldTree and
 * newTree.
 *
 * A node's children are considered to be different if:
 * - the number of children changed
 * - the block IDs of the children changed
 * - any child block changed
 */
function diffTrees(oldTree: Readonly<BlockTree>, newTree: Readonly<BlockTree>) {
	return diffSubtree(oldTree.root, newTree.root);

	function diffSubtree(oldNode: BlockTreeNode, newNode: BlockTreeNode): BlockTreeNode[] {
		const commonChildren = sets.intersection(nodeChildrenBlockIds(oldNode), nodeChildrenBlockIds(newNode));
		const nodesWithChanges = [];

		for (const blockId of commonChildren) {
			const oldChild = oldTree.getNodeByBlockKey(blockId),
				newChild = newTree.getNodeByBlockKey(blockId);

			if (!oldChild || !newChild) continue;
			if (!oldChild.children.length && !newChild.children.length) continue;

			// Check if number of children differs
			if (oldChild.children.length !== newChild.children.length) {
				nodesWithChanges.push(newChild);
			} else {
				// Check if child blockIds changed
				const oldChildChildrenIds = nodeChildrenBlockIds(oldChild);
				const newChildChildrenIds = nodeChildrenBlockIds(newChild);
				if (!sets.equal(oldChildChildrenIds, newChildChildrenIds)) {
					nodesWithChanges.push(newChild);
				} else {
					// Check if any children changed
					for (const blockId of nodeChildrenBlockIds(newChild)) {
						const oldChildChild = oldTree.getNodeByBlockKey(blockId);
						const newChildChild = newTree.getNodeByBlockKey(blockId);
						if (!oldChildChild?.block || !newChildChild?.block) continue;
						if (!oldChildChild.block.eq(newChildChild.block)) {
							nodesWithChanges.push(newChild);
							break;
						}
					}
				}
			}

			nodesWithChanges.push(...diffSubtree(oldChild, newChild));
		}

		return nodesWithChanges;
	}
}

function getBlockBoundaries(doc: ProseNode): Boundaries {
	const newBlockBoundaries: Boundaries = {};
	blockDescendants(doc, (block, pos) => {
		const k = block.attrs.blockId;
		if (!k) return false;
		newBlockBoundaries[k] = [pos, pos + block.nodeSize, block];
		return;
	});
	return newBlockBoundaries;
}

const decorateChangedParentsPluginKey = new PluginKey<DecorationSet | undefined>("decorate-changed-parents");

// Plugin to add node decorations to any internal nodes whose descendants have
// changed. This will cause the NodeView of those internal nodes to be updated
// by ProseMirror so they can respond to changes in their descendants.
const decorateChangedParentsPlugin = new Plugin<Readonly<DecorationSet | undefined>>({
	key: decorateChangedParentsPluginKey,

	state: {
		init: (_config, _state) => undefined,
		apply: (tr, value, _oldState, newState) => {
			if (!tr.docChanged) return value;
			const blockBoundary = getBlockBoundaryState(newState);
			invariant(blockBoundary?.changedInternalTreeNodes);

			// Decorate all parent nodes so that they get updated. This way if their
			// children changed they will be able to update.
			const decorations = [];
			for (const node of blockBoundary.changedInternalTreeNodes) {
				invariant(node.block);
				decorations.push(Decoration.node(node.start, node.start + node.block.nodeSize, {}, { _renderId: v4() }));
			}

			return DecorationSet.create(newState.doc, decorations);
		},
	},

	props: {
		decorations: state => decorateChangedParentsPluginKey.getState(state),
	},
});

type BlockBoundariesPluginState = {
	previousBlockBoundaries: Readonly<Boundaries>;
	blockBoundaries: Readonly<Boundaries>;
	blockTree: Readonly<BlockTree>;
	prevBlockTree: Readonly<BlockTree> | undefined;
	changedInternalTreeNodes: readonly BlockTreeNode[] | undefined;
};
const BlockBoundariesPluginKey = new PluginKey<BlockBoundariesPluginState>("blockBoundaries");
export const getBlockBoundaryState = (editorState: EditorState) => {
	return BlockBoundariesPluginKey.getState(editorState) ?? undefined;
};

class BlockBoundary extends Extension {
	get name() {
		return "BlockBoundary";
	}
	get plugins() {
		const blockBoundariesPlugin = new Plugin<Readonly<BlockBoundariesPluginState>>({
			key: BlockBoundariesPluginKey,
			state: {
				init: (_p, state) => {
					return {
						previousBlockBoundaries: {},
						blockBoundaries: getBlockBoundaries(state.doc),
						blockTree: buildTree(state.doc),
						prevBlockTree: undefined,
						changedInternalTreeNodes: undefined,
					};
				},
				apply: (tr, value, _oldState, newState) => {
					if (tr.docChanged) {
						const prevBlockTree = value.blockTree;
						const blockTree = buildTree(newState.doc);
						return {
							previousBlockBoundaries: value.blockBoundaries,
							blockBoundaries: getBlockBoundaries(newState.doc),
							prevBlockTree,
							blockTree,
							changedInternalTreeNodes: diffTrees(prevBlockTree, blockTree),
						};
					}
					return value;
				},
			},
		});
		return [blockBoundariesPlugin, decorateChangedParentsPlugin];
	}
}

export default BlockBoundary;
